import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getA11yDuration } from '@fec/assets/js/utils/media-progress';
import { getConfig } from '@fec/assets/js/utils/config';

import { getMediumProgressPercentage } from '@fec/assets/js/utils/media-progress';
import { shouldShowMediaProgress } from '@fec/frontend/elements/media/media-progress-bar/media-progress-bar';
import { formatDate } from '@fec/assets/js/utils/date-time-formatter';
import { capitalizeFirstLetter } from '@fec/assets/js/utils/string-formatter';

const usePlayerA11yHelper = (asset, isBlocked, isActive = false) => {
  const { t } = useTranslation();

  const getShowDescription = (show, date) => {
    return show && date
      ? t('basicCaption.videoCaption', {
          show: `«${show}»`,
          date: formatDate(date),
        })
      : '';
  };

  const createMainLabel = (
    asset,
    isBlocked,
    isActive,
    suppressStateSuffix = false,
  ) => {
    const { title, duration, show, date, urn, type } = asset;

    const config = getConfig();
    const representation = config.REPRESENTATION ?? 'web';
    const isWebview = representation.includes('webview');

    // start with type and title
    let mainLabel = `${capitalizeFirstLetter(type)} «${title}»`;

    // add suffixes for playing and pausing
    if (!isBlocked && !suppressStateSuffix) {
      mainLabel = isActive
        ? `${mainLabel} ${t('playerWidget.pauseSuffix')}.`
        : `${mainLabel} ${t('playerWidget.playSuffix')}.`;
    } else {
      mainLabel = `${mainLabel}.`;
    }

    // add show description
    const description = getShowDescription(show, date);
    mainLabel = `${mainLabel} ${description}.`;

    // add duration hint
    if (!isBlocked && duration) {
      const a11yDuration = t('duration', {
        duration: getA11yDuration(duration, t),
      });
      mainLabel = `${mainLabel} ${a11yDuration}.`;
    }

    // add progress hint (for audio)
    if (!isWebview) {
      const progressPercentage = getMediumProgressPercentage(urn);
      if (progressPercentage > 0 && shouldShowMediaProgress()) {
        const progressHint = t('playerWidget.progressHint', {
          percentage: Math.ceil(progressPercentage),
        });
        mainLabel = `${mainLabel} ${progressHint}`;
      }
    }

    return mainLabel;
  };

  return useMemo(() => {
    const { title, type, show, date } = asset;

    // "mainLabel" consists of the type, title, show info and hints about the duration (if
    // known and not blocked) and progress (if medium was started before).
    // It is used for "uninitialized" players to describe their content.
    const mainLabel = createMainLabel(asset, isBlocked, isActive, false);

    // Same as "mainLabel", but without the state suffix (playing/paused). This is mainly used for items
    // that control the player but are not part of the player itself (e.g. a video gallery item).
    const mainLabelWithoutPlayerState = createMainLabel(
      asset,
      isBlocked,
      isActive,
      true,
    );

    // "activePlayerLabel" is used for the letterbox player container itself (-> after
    // initialization of the player). screen readers use it to describe the player
    // region when it's focused.
    const activePlayerLabel = `${capitalizeFirstLetter(type)}-Player «${title}». ${getShowDescription(show, date)}.`;

    // A11Y attributes for player components (in the "web" representation they
    // are only used when the letterbox player is not initialized - after that
    // the letterbox player is directly focusable)
    const a11yAttributes = {
      role: 'button',
      tabIndex: '0',
      'aria-label': mainLabel,
    };

    return {
      mainLabel,
      mainLabelWithoutPlayerState,
      activePlayerLabel,
      a11yAttributes,
    };
  }, [asset, isBlocked, isActive, t]);
};

export default usePlayerA11yHelper;
